import { useState, useCallback, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  SortingState,
  RowSelectionState,
  VisibilityState
} from '@tanstack/react-table';

import { useInfinityScroll } from '@/shared/lib/use-infinity-scroll';

import {
  loadColumnConfigs,
  saveColumnConfigs,
  configsToVisibilityState,
  orderColumns,
  reorderColumnConfigs,
  toggleColumnConfig,
  createDefaultConfigs
} from '../lib/column-utils';
import { idsToRowSelection, rowSelectionToIds, isControlledSelection } from '../lib/selection-utils';
import { convertToTanstackColumns } from '../lib/table-utils';
import { createColumnsMap, getSortFieldFromMap, getColumnIdBySortField } from '../lib/columns-map';
import { DataTableRow, DataTableColumn, ColumnConfig } from '../types';

interface UseDataTableProps<T extends DataTableRow> {
  data: T[];
  columns: DataTableColumn<T>[];
  tableId: string;
  isFetchingNextPage?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  getRowId?: (row: T) => string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
}

export function useDataTable<T extends DataTableRow>({
  data,
  columns,
  tableId,
  isFetchingNextPage,
  hasMore,
  onLoadMore,
  selectedIds = [],
  onSelectionChange,
  getRowId = (row) => row.id,
  sortField,
  sortDirection,
  onSort
}: UseDataTableProps<T>) {
  const loadMoreRef = useInfinityScroll({
    hasMore,
    isLoading: isFetchingNextPage,
    onLoadMore
  });

  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>(() => loadColumnConfigs(tableId, columns));
  const [tempColumnConfigs, setTempColumnConfigs] = useState<ColumnConfig[]>(columnConfigs);

  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const isControlledSorting = !!onSort;
  const controlledSorting = useMemo<SortingState>(() => {
    if (!sortField) return [];
    const columnId = getColumnIdBySortField(columns, sortField);
    return [{ id: columnId, desc: sortDirection === 'desc' }];
  }, [sortField, sortDirection, columns]);

  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const sorting = isControlledSorting ? controlledSorting : localSorting;

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    configsToVisibilityState(columnConfigs)
  );

  const isControlled = isControlledSelection(onSelectionChange);
  const [localRowSelection, setLocalRowSelection] = useState<RowSelectionState>({});

  const controlledRowSelection = useMemo(
    () => (isControlled ? idsToRowSelection(selectedIds) : {}),
    [selectedIds, isControlled]
  );

  const rowSelection = isControlled ? controlledRowSelection : localRowSelection;

  const orderedColumns = useMemo(() => orderColumns(columns, columnConfigs), [columns, columnConfigs]);
  const tableColumns = useMemo(() => convertToTanstackColumns(orderedColumns), [orderedColumns]);
  const columnsMap = useMemo(() => createColumnsMap(orderedColumns), [orderedColumns]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnVisibility,
      rowSelection
    },
    onSortingChange: (updater) => {
      if (isControlledSorting) {
        const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
        if (newSorting.length > 0) {
          const { id, desc } = newSorting[0];
          const sortFieldId = getSortFieldFromMap(columnsMap, id);
          onSort(sortFieldId, desc ? 'desc' : 'asc');
        } else if (sortField) {
          // Clear sorting
          onSort('', 'asc');
        }
      } else {
        setLocalSorting(updater);
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: (updater) => {
      if (isControlled) {
        const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
        const selectedIds = rowSelectionToIds(newSelection);
        onSelectionChange!(selectedIds);
      } else {
        setLocalRowSelection(updater);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isControlledSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualSorting: isControlledSorting,
    getRowId,
    enableRowSelection: true,
    enableMultiRowSelection: true
  });

  const handleReorderColumns = useCallback((activeId: string, overId: string) => {
    setTempColumnConfigs((configs) => reorderColumnConfigs(configs, activeId, overId));
  }, []);

  const handleToggleColumnVisibility = useCallback((columnId: string) => {
    setTempColumnConfigs((configs) => toggleColumnConfig(configs, columnId));
  }, []);

  const openColumnCustomization = useCallback(() => {
    setTempColumnConfigs(columnConfigs);
    setIsCustomizeOpen(true);
  }, [columnConfigs]);

  const saveColumnCustomization = useCallback(() => {
    setColumnConfigs(tempColumnConfigs);
    setColumnVisibility(configsToVisibilityState(tempColumnConfigs));
    saveColumnConfigs(tableId, tempColumnConfigs);
    setIsCustomizeOpen(false);
  }, [tempColumnConfigs, tableId]);

  const cancelColumnCustomization = useCallback(() => {
    setTempColumnConfigs(columnConfigs);
    setIsCustomizeOpen(false);
  }, [columnConfigs]);

  const resetColumnCustomization = useCallback(() => {
    setTempColumnConfigs(createDefaultConfigs(columns));
  }, [columns]);

  const applyPreset = useCallback(
    (presetIds: string[]) => {
      setTempColumnConfigs(() => {
        // First, get the default configurations (reset order/state)
        const defaultConfigs = createDefaultConfigs(columns);

        // Then, update visibility based on presetIds
        const updated = defaultConfigs.map((config) => ({
          ...config,
          visible: presetIds.includes(config.id)
        }));

        return updated;
      });
    },
    [columns]
  );

  const rollbackColumnCustomization = useCallback(() => {
    setTempColumnConfigs(columnConfigs);
  }, [columnConfigs]);

  return {
    // Table instance
    table,

    // Refs
    loadMoreRef,

    // UI States
    isCustomizeOpen,
    isFullscreenOpen,
    tempColumnConfigs: [...tempColumnConfigs].sort((a, b) => a.order - b.order),

    // UI Actions
    setIsCustomizeOpen,
    setIsFullscreenOpen,
    reorderColumns: handleReorderColumns,
    toggleColumnVisibility: handleToggleColumnVisibility,
    openColumnCustomization,
    saveColumnCustomization,
    cancelColumnCustomization,
    resetColumnCustomization,
    applyPreset,
    rollbackColumnCustomization
  };
}
